// Firebase configuration and initialization
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
// import { connectFirestoreEmulator } from 'firebase/firestore';
// import { connectAuthEmulator } from 'firebase/auth';
// import { connectStorageEmulator } from 'firebase/storage';

// Firebase configuration using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore
const db = getFirestore(app);

// Initialize Auth
const auth = getAuth(app);

// Initialize Storage
const storage = getStorage(app);

// Initialize Analytics only if measurementId exists and we're not in development
let analytics = null;
if (firebaseConfig.measurementId && typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
    console.log('Firebase Analytics initialized successfully');
  } catch (error) {
    console.warn('Analytics initialization failed:', error);
  }
}

// Connect to emulators in development
if (process.env.NODE_ENV === 'development') {
  // Uncomment these lines if you want to use Firebase emulators
  // connectFirestoreEmulator(db, 'localhost', 8080);
  // connectAuthEmulator(auth, 'http://localhost:9099');
  // connectStorageEmulator(storage, 'localhost', 9199);
}

// Analytics helper functions
export const trackEvent = (eventName, parameters = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, parameters);
  }
};

export const trackPageView = (pageName, pageTitle = '') => {
  if (analytics) {
    logEvent(analytics, 'page_view', {
      page_title: pageTitle || pageName,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }
};

export const trackUserEngagement = (engagementTimeMs) => {
  if (analytics) {
    logEvent(analytics, 'user_engagement', {
      engagement_time_msec: engagementTimeMs
    });
  }
};

export const trackCustomEvent = (eventName, customParameters = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, {
      ...customParameters,
      timestamp: Date.now()
    });
  }
};

export const setAnalyticsUserId = (userId) => {
  if (analytics) {
    setUserId(analytics, userId);
  }
};

export const setAnalyticsUserProperties = (properties) => {
  if (analytics) {
    setUserProperties(analytics, properties);
  }
};

// Auth helper functions
export const signIn = async (email, password) => {
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  return signInWithEmailAndPassword(auth, email, password);
};

export const signUp = async (email, password) => {
  const { createUserWithEmailAndPassword } = await import('firebase/auth');
  return createUserWithEmailAndPassword(auth, email, password);
};

export const signOut = async () => {
  const { signOut: firebaseSignOut } = await import('firebase/auth');
  return firebaseSignOut(auth);
};

export const resetPassword = async (email) => {
  const { sendPasswordResetEmail } = await import('firebase/auth');
  return sendPasswordResetEmail(auth, email);
};

// Auth event tracking helpers
export const trackAuthEvent = (eventName, additionalParams = {}) => {
  trackCustomEvent(`auth_${eventName}`, {
    ...additionalParams,
    timestamp: Date.now()
  });
};

export const trackLoginSuccess = (method = 'email') => {
  trackAuthEvent('login_success', { method });
};

export const trackSignupSuccess = (method = 'email') => {
  trackAuthEvent('signup_success', { method });
};

export const trackLogout = () => {
  trackAuthEvent('logout');
};

export const trackPasswordReset = () => {
  trackAuthEvent('password_reset_requested');
};

export { analytics, db, auth, storage };
export default app;